import type { Customer } from 'src/shared/types';
import { AppViews, PortfolioViews } from 'src/routes/routesConfig';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { PRODUCT_RELEASE, ProductKey, ProductVisibility } from 'src/shared/types/engine/products';
import { ProductAccess } from 'src/shared/types/productLicensing';
import { productAccess } from 'src/shared/utils/productAccess';

/** Where a user lands when they enter an organisation. */
export enum LandingPage {
    GNR = 'Global Nature Risk',
    GNP = 'Global Nature Performance',
    MY_SITES = 'My Sites',
}

type LandingSituation = {
    hasPortfolio: boolean;
    isGnrVisible: boolean;
    wasOnGnpPage: boolean;
};

/**
 * Where a user goes when they enter an organisation. They stay in Global Nature Performance when the
 * organisation has a portfolio. Otherwise the order is Global Nature Risk, then Global Nature
 * Performance, then My Sites. Global Nature Risk needs no rule of its own to keep its users, because
 * it is first in that order.
 */
export const landingPage = ({ hasPortfolio, isGnrVisible, wasOnGnpPage }: LandingSituation): LandingPage => {
    if (wasOnGnpPage && hasPortfolio) {
        return LandingPage.GNP;
    } else if (isGnrVisible) {
        return LandingPage.GNR;
    } else if (hasPortfolio) {
        return LandingPage.GNP;
    } else {
        return LandingPage.MY_SITES;
    }
};

type GnpSituation = {
    hasPortfolio: boolean;
    /** The Global Nature Performance setting of the organisation. `undefined` is the default. */
    setting: ProductVisibility | undefined;
    isInternal: boolean;
};

/**
 * Whether the user sees Global Nature Performance in an organisation: it has
 * a project in a portfolio, and the product is not set to Hidden for a
 * customer. For the landing page, before the app has switched to the
 * organisation, so it cannot use `useProductAccess`.
 */
export const isGnpShown = ({ hasPortfolio, setting, isInternal }: GnpSituation): boolean =>
    productAccess({
        release: PRODUCT_RELEASE[ProductKey.GLOBAL_NATURE_PERFORMANCE],
        setting,
        isInternal,
        hasResults: hasPortfolio,
    }) === ProductAccess.LICENSED;

/** The organisation the app selects when the user has not chosen one: the first that is not the demo. */
export const defaultCustomerId = (customers: Pick<Customer, 'customerId'>[]): string | undefined =>
    customers.find(customer => customer.customerId !== 'Extractives Customer')?.customerId ?? customers[0]?.customerId;

export const landingPath = (landing: LandingPage, customerId: string): string => {
    if (landing === LandingPage.GNR) {
        return navRoutesConfig[PortfolioViews.GLOBAL_NATURE_RISK_OVERVIEW]({ customerId });
    } else if (landing === LandingPage.GNP) {
        return navRoutesConfig[PortfolioViews.PORTFOLIO_SUMMARY]({ customerId });
    } else {
        return navRoutesConfig[AppViews.PROJECTS]({ customerId });
    }
};
